import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Layout, { Overline } from "../components/Layout";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Motion";
import { IMAGES, MATRIX_COLUMNS, MATRIX_ROWS } from "../data/site";

const levelTone = ["bg-sand text-charcoal", "bg-eucalyptus-light text-warmwhite", "bg-eucalyptus-deep text-warmwhite"];

export default function CapabilityFramework() {
  const [hoverRow, setHoverRow] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);

  return (
    <Layout
      title="Ranger Capability Framework"
      description="The Ranger Base capability matrix maps how ranger programs grow from Foundation to Advanced across leadership, ecology, carbon, seed systems, cultural heritage, monitoring, GIS, planning, governance and enterprise."
    >
      <PageHero
        overline="Ranger Capability Framework"
        titleLines={["From foundation", "to stewardship"]}
        subtitle="We partner with organisations over multiple years to progressively build complete ranger capability, one level at a time."
        image={IMAGES.canopy}
      />

      <section className="py-20 lg:py-28 bg-warmwhite">
        <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
          <div className="max-w-3xl mb-14">
            <div className="text-eucalyptus mb-6"><Overline>The Matrix</Overline></div>
            <Reveal>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter text-charcoal leading-[0.95]">
                Ten capabilities. Three levels. One long partnership.
              </h2>
              <p className="mt-5 text-lg text-charcoal/70 leading-relaxed">
                Hover any row or column to trace how capability develops. Every organisation starts in a
                different place — the framework simply shows the road ahead.
              </p>
            </Reveal>
          </div>

          {/* Matrix */}
          <div className="overflow-x-auto -mx-6 px-6 lg:mx-0 lg:px-0">
            <div className="min-w-[820px]">
              {/* Header */}
              <div className="grid grid-cols-[180px_repeat(3,1fr)] gap-3 mb-3">
                <div />
                {MATRIX_COLUMNS.map((c, ci) => (
                  <div
                    key={c}
                    onMouseEnter={() => setHoverCol(ci)} onMouseLeave={() => setHoverCol(null)}
                    className={`rounded-2xl px-5 py-4 text-center font-heading font-semibold tracking-tight transition-colors ${hoverCol === ci ? "bg-charcoal text-warmwhite" : "bg-charcoal/5 text-charcoal"}`}
                  >
                    {c}
                  </div>
                ))}
              </div>
              {/* Rows */}
              <div className="space-y-3">
                {MATRIX_ROWS.map((r, ri) => (
                  <div key={r.area} className="grid grid-cols-[180px_repeat(3,1fr)] gap-3" onMouseEnter={() => setHoverRow(ri)} onMouseLeave={() => setHoverRow(null)}>
                    <div className={`rounded-2xl px-5 py-4 flex items-center font-heading font-semibold tracking-tight transition-colors ${hoverRow === ri ? "bg-ochre text-white" : "bg-eucalyptus text-warmwhite"}`}>
                      {r.area}
                    </div>
                    {r.levels.map((lvl, li) => {
                      const active = hoverRow === ri || hoverCol === li;
                      return (
                        <div
                          key={li}
                          data-testid={`matrix-cell-${ri}-${li}`}
                          className={`rounded-2xl px-5 py-4 text-sm leading-snug transition-[transform,box-shadow] duration-300 ${levelTone[li]} ${active ? "scale-[1.02] shadow-lg z-10 relative" : "opacity-90"}`}
                        >
                          {lvl}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-6 text-sm text-charcoal/50">Scroll horizontally on smaller screens to view the full matrix.</p>
        </div>
      </section>

      {/* Approach */}
      <section className="py-24 bg-sand">
        <div className="mx-auto max-w-[88rem] px-6 lg:px-10 grid lg:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Meet you where you are", d: "We begin with an honest capacity assessment — no judgement, just a clear picture." },
            { n: "02", t: "Build level by level", d: "We work through the framework at a pace that fits your team and your Country." },
            { n: "03", t: "Hand back the capability", d: "Skills, systems and confidence stay with your organisation for the long term." },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="h-full rounded-3xl bg-white border border-charcoal/10 p-8">
                <span className="font-mono text-ochre">{s.n}</span>
                <h3 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-charcoal">{s.t}</h3>
                <p className="mt-3 text-charcoal/65 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 bg-warmwhite">
        <div className="mx-auto max-w-[80rem] px-6 lg:px-10 rounded-[2rem] bg-eucalyptus text-warmwhite p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 relative overflow-hidden">
          <div className="grain absolute inset-0 opacity-30" />
          <h2 className="relative font-heading text-3xl md:text-4xl font-semibold tracking-tight max-w-xl">Map your program's next few years with us.</h2>
          <Link to="/contact" data-testid="capability-cta" className="relative inline-flex items-center gap-2 rounded-full bg-warmwhite text-eucalyptus-deep px-7 py-4 font-semibold hover:bg-sandstone transition-colors shrink-0">
            Start your assessment <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
